import React, { useState, useEffect, useRef } from 'react';
import { PlayIcon, PauseIcon, SpeakerWaveIcon, SpeakerXMarkIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import { authorizedFetch } from '../utils/api';

// Video Player Component
function VideoPlayer({ video, isActive, onVideoEnd }) {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [showControls, setShowControls] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);

    useEffect(() => {
        const videoElement = videoRef.current;
        if (!videoElement) return;

        const handleTimeUpdate = () => setCurrentTime(videoElement.currentTime);
        const handleDurationChange = () => setDuration(videoElement.duration);
        const handleEnded = () => {
            setIsPlaying(false);
            if (onVideoEnd) onVideoEnd();
        };

        videoElement.addEventListener('timeupdate', handleTimeUpdate);
        videoElement.addEventListener('durationchange', handleDurationChange);
        videoElement.addEventListener('ended', handleEnded);

        return () => {
            videoElement.removeEventListener('timeupdate', handleTimeUpdate);
            videoElement.removeEventListener('durationchange', handleDurationChange);
            videoElement.removeEventListener('ended', handleEnded);
        };
    }, [onVideoEnd]);

    const togglePlay = () => {
        const videoElement = videoRef.current;
        if (isPlaying) {
            videoElement.pause();
        } else {
            videoElement.play();
        }
        setIsPlaying(!isPlaying);
    };

    const toggleMute = () => {
        const videoElement = videoRef.current;
        videoElement.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const handleSeek = (e) => {
        const videoElement = videoRef.current;
        const rect = e.currentTarget.getBoundingClientRect();
        const pos = (e.clientX - rect.left) / rect.width;
        videoElement.currentTime = pos * duration;
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        videoRef.current.volume = newVolume;
    };

    const toggleFullscreen = () => {
        const videoElement = videoRef.current;
        if (!isFullscreen) {
            if (videoElement.requestFullscreen) {
                videoElement.requestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
        setIsFullscreen(!isFullscreen);
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return (
        <div
            className="relative bg-black rounded-lg overflow-hidden group w-full h-full"
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
        >
            {video.youtube_id ? (
                <div className="w-full h-full aspect-video">
                    <iframe
                        src={`https://www.youtube.com/embed/${video.youtube_id}?autoplay=1&controls=1&rel=0&modestbranding=1`}
                        title={`${video.brand} ${video.collection_name}`}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        onLoad={() => console.log('YouTube iframe loaded successfully')}
                        onError={() => console.log('YouTube iframe failed to load')}
                    />
                </div>
            ) : (
                <video
                    ref={videoRef}
                    src={video.video_url}
                    poster={video.thumbnail_url}
                    className="w-full h-full object-cover cursor-pointer"
                    onClick={togglePlay}
                />
            )}

            {/* Video Overlay Info */}
            <div className="absolute top-4 left-4 bg-black bg-opacity-70 text-white px-3 py-2 rounded-lg">
                <h3 className="font-semibold text-sm">{video.brand}</h3>
                <p className="text-xs opacity-90">{video.collection_name}</p>
                <p className="text-xs opacity-75">{video.season} {video.year}</p>
            </div>

            {/* Play/Pause Overlay - Only show for non-YouTube videos */}
            {!video.youtube_id && !isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center">
                    <button
                        onClick={togglePlay}
                        className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-4 transition-all"
                    >
                        <PlayIcon className="w-12 h-12 text-white ml-1" />
                    </button>
                </div>
            )}

            {/* Video Controls - Only show for non-YouTube videos */}
            {!video.youtube_id && showControls && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
                    {/* Progress Bar */}
                    <div
                        className="w-full h-2 bg-gray-600 rounded-full mb-3 cursor-pointer"
                        onClick={handleSeek}
                    >
                        <div
                            className="h-full bg-white rounded-full transition-all"
                            style={{ width: `${(currentTime / duration) * 100}%` }}
                        />
                    </div>

                    {/* Control Buttons */}
                    <div className="flex items-center justify-between text-white">
                        <div className="flex items-center gap-3">
                            <button onClick={togglePlay} className="hover:text-blue-400 transition-colors">
                                {isPlaying ? (
                                    <PauseIcon className="w-6 h-6" />
                                ) : (
                                    <PlayIcon className="w-6 h-6" />
                                )}
                            </button>

                            <button onClick={toggleMute} className="hover:text-blue-400 transition-colors">
                                {isMuted ? (
                                    <SpeakerXMarkIcon className="w-6 h-6" />
                                ) : (
                                    <SpeakerWaveIcon className="w-6 h-6" />
                                )}
                            </button>

                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                value={volume}
                                onChange={handleVolumeChange}
                                className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer"
                            />

                            <span className="text-sm">
                                {formatTime(currentTime)} / {formatTime(duration)}
                            </span>
                        </div>

                        <button onClick={toggleFullscreen} className="hover:text-blue-400 transition-colors">
                            <ArrowsPointingOutIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// Video Card Component for Grid View
function VideoCard({ video, onClick, isSelected }) {
    return (
        <div
            className={`group relative bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-2xl ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
            onClick={() => onClick(video)}
        >
            <div className="relative aspect-video overflow-hidden">
                <img
                    src={video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_id}/maxresdefault.jpg`}
                    alt={`${video.brand} ${video.collection_name}`}
                    className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => {
                        // Fallback to medium quality thumbnail if maxres fails
                        if (e.target.src.includes('maxresdefault')) {
                            e.target.src = `https://img.youtube.com/vi/${video.youtube_id}/hqdefault.jpg`;
                        } else {
                            e.target.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg';
                        }
                    }}
                />

                {/* Brand Badge */}
                <div className="absolute top-3 left-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                    {video.brand}
                </div>

                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <div className="bg-white bg-opacity-20 rounded-full p-4 transform transition-transform duration-300 group-hover:scale-110">
                        <PlayIcon className="w-10 h-10 text-white" />
                    </div>
                </div>

                {/* Duration Badge */}
                {video.duration && (
                    <div className="absolute bottom-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded-full text-xs font-medium">
                        {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                    </div>
                )}
            </div>

            <div className="p-5">
                <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-900 dark:text-white text-lg">{video.collection_name}</h3>
                    {video.fashion_week && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                            {video.fashion_week}
                        </span>
                    )}
                </div>

                <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-3">
                    <span className="mr-3">{video.season} {video.year}</span>
                    {video.designer && (
                        <span className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {video.designer}
                        </span>
                    )}
                </div>

                {video.location && (
                    <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {video.location}
                    </div>
                )}
            </div>
        </div>
    );
}

// Filter Component
function VideoFilters({ filters, onFilterChange, brands, seasons, years, fashionWeeks, isVisible, onToggle }) {
    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden transition-all duration-300 ease-in-out">
            <div
                className="flex justify-between items-center p-4 cursor-pointer bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800"
                onClick={onToggle}
            >
                <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                    </svg>
                    Filter Options
                </h3>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-5 w-5 transition-transform duration-300 ${isVisible ? 'transform rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            <div className={`p-4 border-t border-gray-200 dark:border-gray-700 ${isVisible ? 'block' : 'hidden'}`}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Brand
                        </label>
                        <select
                            value={filters.brand}
                            onChange={(e) => onFilterChange({ ...filters, brand: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200"
                        >
                            <option value="">All Brands</option>
                            {brands.map(brand => (
                                <option key={brand} value={brand}>{brand}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Season
                        </label>
                        <select
                            value={filters.season}
                            onChange={(e) => onFilterChange({ ...filters, season: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200"
                        >
                            <option value="">All Seasons</option>
                            {seasons.map(season => (
                                <option key={season} value={season}>{season}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Year
                        </label>
                        <select
                            value={filters.year}
                            onChange={(e) => onFilterChange({ ...filters, year: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200"
                        >
                            <option value="">All Years</option>
                            {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Fashion Week
                        </label>
                        <select
                            value={filters.fashion_week}
                            onChange={(e) => onFilterChange({ ...filters, fashion_week: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200"
                        >
                            <option value="">All Fashion Weeks</option>
                            {fashionWeeks.map(week => (
                                <option key={week} value={week}>{week}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Sort By
                        </label>
                        <select
                            value={filters.sortBy}
                            onChange={(e) => onFilterChange({ ...filters, sortBy: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200"
                        >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                            <option value="brand">Brand A-Z</option>
                            <option value="duration">Duration</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Main Videos Component
export default function Videos() {
    // This function is used to prevent page scroll when navigating between tabs or scoring looks
    useEffect(() => {
        // Save the current scroll position when component mounts
        const scrollPosition = window.pageYOffset;

        // Create a function to maintain scroll position
        const maintainScrollPosition = () => {
            // Store the current scroll position in sessionStorage
            sessionStorage.setItem('scrollPosition', window.pageYOffset);
        };

        // Add event listener to store scroll position before page refreshes
        window.addEventListener('beforeunload', maintainScrollPosition);

        // Check if there's a stored scroll position and restore it
        const savedPosition = sessionStorage.getItem('scrollPosition');
        if (savedPosition) {
            window.scrollTo(0, parseInt(savedPosition));
            // Clear the stored position after using it
            sessionStorage.removeItem('scrollPosition');
        }

        return () => {
            // Clean up event listener
            window.removeEventListener('beforeunload', maintainScrollPosition);
        };
    }, []);

    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'theater'
    const [filters, setFilters] = useState({
        brand: '',
        season: '',
        year: '',
        fashion_week: '',
        sortBy: 'newest'
    });
    const [brands, setBrands] = useState([]);
    const [seasons, setSeasons] = useState([]);
    const [years, setYears] = useState([]);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [fashionWeeks, setFashionWeeks] = useState([]);
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        const fetchVideos = async () => {
            setLoading(true);
            try {
                // Fetch videos from the API
                const response = await authorizedFetch('/fashion-videos/latest');
                const data = await response.json();

                if (data.videos) {
                    setVideos(data.videos);
                    if (data.videos.length > 0) {
                        setSelectedVideo(data.videos[0]);
                    }
                }

                // Fetch filter options
                const filtersResponse = await authorizedFetch('/fashion-videos/filters/options');
                const filtersData = await filtersResponse.json();

                if (filtersData.brands) setBrands(filtersData.brands);
                if (filtersData.seasons) setSeasons(filtersData.seasons);
                if (filtersData.years) setYears(filtersData.years);
                if (filtersData.fashion_weeks) setFashionWeeks(filtersData.fashion_weeks);

                setLoading(false);
            } catch (err) {
                console.error('Error fetching videos:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        fetchVideos();
    }, []);

    const filteredVideos = videos.filter(video => {
        return (
            (!filters.brand || video.brand === filters.brand) &&
            (!filters.season || video.season === filters.season) &&
            (!filters.year || video.year.toString() === filters.year) &&
            (!filters.fashion_week || video.fashion_week === filters.fashion_week)
        );
    }).sort((a, b) => {
        switch (filters.sortBy) {
            case 'newest':
                return new Date(b.created_at) - new Date(a.created_at);
            case 'oldest':
                return new Date(a.created_at) - new Date(b.created_at);
            case 'brand':
                return a.brand.localeCompare(b.brand);
            case 'duration':
                return (b.duration || 0) - (a.duration || 0);
            default:
                return 0;
        }
    });

    const handleVideoSelect = (video) => {
        setSelectedVideo(video);
        setCurrentVideoIndex(filteredVideos.findIndex(v => v.id === video.id));
        setViewMode('theater'); // Automatically switch to theater mode when a video is clicked
    };

    const handleVideoEnd = () => {
        // Auto-play next video
        const nextIndex = (currentVideoIndex + 1) % filteredVideos.length;
        setCurrentVideoIndex(nextIndex);
        setSelectedVideo(filteredVideos[nextIndex]);
    };

    if (loading) {
        return (
            <div className="p-6">
                <div className="animate-pulse space-y-6">
                    <div className="h-8 bg-gray-300 rounded w-1/4"></div>
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden">
                                <div className="aspect-video bg-gray-300"></div>
                                <div className="p-4 space-y-2">
                                    <div className="h-4 bg-gray-300 rounded"></div>
                                    <div className="h-3 bg-gray-200 rounded"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 text-center">
                <div className="text-red-500 mb-4">
                    <h2 className="text-xl font-semibold">Error Loading Videos</h2>
                    <p>{error}</p>
                </div>
                <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen w-full bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 shadow-md w-full">
                <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
                        <div>
                            <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
                                Fashion Show Videos 🎬
                            </h1>
                            <p className="text-gray-600 dark:text-gray-400 mt-2">
                                Watch Spring Summer 2025 collections from London and New York Fashion Weeks
                            </p>
                        </div>

                        <div className="flex space-x-3">
                            {/* Filter Toggle Button */}
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                </svg>
                                Filters
                            </button>

                            {/* View Mode Toggle */}
                            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'grid'
                                        ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                                        : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                                        }`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setViewMode('theater')}
                                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${viewMode === 'theater'
                                        ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                                        : 'text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400'
                                        }`}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">
                {/* Filters */}
                <VideoFilters
                    filters={filters}
                    onFilterChange={setFilters}
                    brands={brands}
                    seasons={seasons}
                    years={years}
                    fashionWeeks={fashionWeeks}
                    isVisible={showFilters}
                    onToggle={() => setShowFilters(!showFilters)}
                />

                {/* Theater Mode */}
                {viewMode === 'theater' && selectedVideo && (
                    <div className="space-y-6 w-full">
                        <div className="aspect-video w-full rounded-xl overflow-hidden shadow-2xl">
                            <VideoPlayer
                                video={selectedVideo}
                                isActive={true}
                                onVideoEnd={handleVideoEnd}
                            />
                        </div>

                        {/* Video Info */}
                        <div className="w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
                            <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {selectedVideo.brand} - {selectedVideo.collection_name}
                                </h2>

                                <div className="mt-2 md:mt-0">
                                    {selectedVideo.fashion_week && (
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                                            {selectedVideo.fashion_week}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                    <span>{selectedVideo.designer || 'Unknown Designer'}</span>
                                </div>

                                <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span>{selectedVideo.season} {selectedVideo.year}</span>
                                </div>

                                <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    <span>{selectedVideo.location || 'Unknown Location'}</span>
                                </div>
                            </div>

                            {selectedVideo.description && (
                                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">About this Show</h3>
                                    <p className="text-gray-700 dark:text-gray-300">{selectedVideo.description}</p>
                                </div>
                            )}

                            <div className="mt-6 flex justify-between items-center">
                                <div className="flex items-center text-gray-600 dark:text-gray-400">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>{Math.floor((selectedVideo.duration || 0) / 60)}:{((selectedVideo.duration || 0) % 60).toString().padStart(2, '0')}</span>
                                </div>

                                <button
                                    onClick={() => setViewMode('grid')}
                                    className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                    </svg>
                                    Back to Grid
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Video Grid */}
                {viewMode === 'grid' && (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
                            {filteredVideos.map((video) => (
                                <VideoCard
                                    key={video.id}
                                    video={video}
                                    onClick={handleVideoSelect}
                                    isSelected={selectedVideo?.id === video.id}
                                />
                            ))}
                        </div>

                        {/* Results Info */}
                        {filteredVideos.length > 0 ? (
                            <div className="text-center text-gray-600 dark:text-gray-400 mt-6">
                                Showing {filteredVideos.length} of {videos.length} fashion shows
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="mx-auto w-24 h-24 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-1">No videos match your filters</h3>
                                <p className="text-gray-600 dark:text-gray-400">Try adjusting your filter criteria or browse all videos</p>
                                <button
                                    onClick={() => setFilters({
                                        brand: '',
                                        season: '',
                                        year: '',
                                        fashion_week: '',
                                        sortBy: 'newest'
                                    })}
                                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Clear All Filters
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
